import { yupResolver } from "@hookform/resolvers/yup";
import { AxiosError } from "axios";
import clsx from "clsx";
import { FormProvider, Resolver, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "@cms/components/data/Button";
import { ControlledSystemPasswordInput } from "@cms/components/data/TextInput";
import { useSnackbar } from "@cms/providers/SnackbarProvider";
import { getSetPasswordSchema, SetPasswordFormData } from "@cms/utils/forms/auth";
import { useAppDispatch, useAppSelector } from "@cms/utils/store/hooks";
import { setPasswordAsyncAction } from "@cms/utils/store/slices/user.slice";
import { IAuthResponse, ISystemApiError } from "@dl/declarations";
import { Routes } from "../../utils/routes";

const SetPasswordPage = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { search } = useLocation();
    const { showSnackbar } = useSnackbar();
    const { profile, isLoading } = useAppSelector((state) => state.user);

    const isPasswordSet = profile.isPasswordSet === true;
    const schema = getSetPasswordSchema(isPasswordSet);

    const methods = useForm<SetPasswordFormData>({
        defaultValues: {
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
        },
        mode: "all",
        resolver: yupResolver(schema) as Resolver<SetPasswordFormData>,
    });

    const {
        watch,
        handleSubmit,
        formState
    } = methods;

    const [newPassword] = watch(["newPassword"]);

    const onSubmit = (data: SetPasswordFormData) => {
        dispatch(
            setPasswordAsyncAction({
                ...(isPasswordSet && { currentPassword: data.currentPassword }),
                newPassword: data.newPassword,
            })
        )
            .then((data) => {
                if (data.meta.requestStatus === "fulfilled") {
                    const payload = data.payload as IAuthResponse;
                    const profile = payload.profile;

                    showSnackbar("Password saved successfully", "success");

                    let pathname = Routes.OVERVIEW;

                    if (!profile.isTwoFactorSecretSetup || !profile.isTwoFactorSecretVerified) {
                        pathname = Routes.TWO_FA;
                    }

                    navigate({ pathname, search });
                } else {
                    const error = data.payload as AxiosError<ISystemApiError>
                    showSnackbar(
                        error?.response?.data?.message || "Failed to set password.",
                        "error"
                    );
                }
            });
    }

    return (
        <>
            <h2 className="text-2xl font-semibold mb-2">
                {isPasswordSet ? "Change password" : "Let's start by setting your password"}
            </h2>

            <p className="mb-6">
                {isPasswordSet
                    ? "Enter your current password and choose a new one."
                    : "This is a mandatory step that allows you to access the CMS in the future if your Google access fails."}
            </p>

            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div className="space-y-2">
                        {isPasswordSet && (
                            <ControlledSystemPasswordInput
                                name="currentPassword"
                                description="Current password"
                                autoComplete="current-password"
                            />
                        )}

                        <ControlledSystemPasswordInput
                            name="newPassword"
                            description="Password"
                            autoComplete="new-password"
                        />

                        <ControlledSystemPasswordInput
                            name="confirmPassword"
                            description="Confirm password"
                            autoComplete="new-password"
                        />
                    </div>


                    <div className="space-y-2">
                        <p>
                            Your password needs to meet the following requirements:
                        </p>
                        <ul className="text-sm">
                            {[
                                {
                                    message: "At least 8 characters long",
                                    isValid: newPassword.length >= 8,
                                },
                                {
                                    message: "Include one uppercase letter",
                                    isValid: /(?=.*[A-Z])/.test(newPassword),
                                },
                                {
                                    message: "Include one lowercase letter",
                                    isValid: /(?=.*[a-z])/.test(newPassword),
                                },
                                {
                                    message: "Include one number",
                                    isValid: /(?=.*\d)/.test(newPassword),
                                },
                                {
                                    message: "Include one special character",
                                    isValid: /(?=.*[^a-zA-Z0-9])/.test(newPassword),
                                    // isValid: /(?=.*[@$!%*?&_])/.test(newPassword),
                                },
                            ].map((validation) => (
                                <li
                                    key={`${validation.message}-${validation.isValid}`}
                                    className={clsx("flex items-center gap-2", validation.isValid ? "text-green-500" : "text-red-500")}
                                >
                                    <span className="text-lg leading-none">{validation.isValid ? "✓" : "✕"}</span>
                                    <span>{validation.message}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="flex justify-center gap-2">
                        <Button
                            type="submit"
                            isLoading={isLoading}
                            inputVariant="white_text_blue_bg"
                            inputSize="full"
                            className="w-1/2"
                            disabled={!formState.isValid}
                        >
                            Save password
                        </Button>

                        {isPasswordSet && (
                            <Button
                                as="link"
                                inputVariant="deep_ocean_text_white_bg"
                                to={Routes.OVERVIEW}
                                inputSize="full"
                                className="w-1/2"
                            >
                                Cancel
                            </Button>
                        )}
                    </div>
                </form>
            </FormProvider>
        </>
    );
};

export default SetPasswordPage;
